/**
 * How a business is booked in its accountant's software: which tool, which accounts, which tax
 * codes. Stored as JSON on `workspaces.accountingExport` and read through `parseExportSettings`,
 * which fills every gap with a default - so an old or half-filled value never breaks an export.
 *
 * The tax codes are the tools' published standard values. Each Kanzlei maps them in its own
 * setup (BMD: MWST-Buchungsmatrix), which is why every code can be overridden in the settings.
 */
import { z } from "zod";
import type { TaxTreatment } from "@/lib/at/vat";

export const EXPORT_TOOLS = ["bmd", "rzl", "datev"] as const;
export type ExportTool = (typeof EXPORT_TOOLS)[number];

/** Incoming (ER, KA) and outgoing (AR) documents get different codes for the same treatment. */
export type Direction = "in" | "out";
export type TaxCodeKey = `${Direction}.${TaxTreatment}`;

/**
 * BMD "BuErf" standard steuercodes (dvo, Cosmoconsult and Domonda mappings agree): 2 Vorsteuer,
 * 1 Umsatzsteuer, 9 ig. Erwerb with input tax, 19 reverse charge with input tax, 7 ig. Lieferung,
 * 5 Ausfuhr, 77 EU service sold, 16 Kleinunternehmer. Empty means "no tax code" (no VAT).
 */
export const BMD_TAX_CODES: Record<TaxCodeKey, string> = {
  "in.standard": "2",
  "in.reverse_charge": "19",
  "in.eu_intra_community": "9",
  "in.export": "2",
  "in.small_business": "",
  "in.no_vat": "",
  "out.standard": "1",
  "out.reverse_charge": "77",
  "out.eu_intra_community": "7",
  "out.export": "5",
  "out.small_business": "16",
  "out.no_vat": "",
};

export const TAX_CODE_KEYS = Object.keys(BMD_TAX_CODES) as TaxCodeKey[];

/**
 * DATEV BU-Schlüssel for Austrian clients, per treatment and - where the key carries it - rate.
 * Defaults are what the dvo import handbook maps for DATEV Austria (DATEV's own table was not
 * reachable): Vorsteuer 9/8/6, Umsatzsteuer 3/2/4, ig. Erwerb 19/18/16, reverse charge 94/91,
 * ig. Lieferung 11, steuerfrei mit Vorsteuerabzug 1. Nothing is known for 4,9 % or for EU services
 * we sell, so those stay empty and keep a document out until the Kanzlei names its key (50-59).
 */
export const DATEV_RATES = [20, 13, 10, 4.9] as const;
export type DatevKeyId =
  | `in.standard.${(typeof DATEV_RATES)[number]}`
  | `out.standard.${(typeof DATEV_RATES)[number]}`
  | `in.eu_intra_community.${(typeof DATEV_RATES)[number]}`
  | `in.reverse_charge.${(typeof DATEV_RATES)[number]}`
  | "out.export"
  | "out.eu_intra_community"
  | "out.reverse_charge";

export const DATEV_KEYS: Record<DatevKeyId, string> = {
  "in.standard.20": "9",
  "in.standard.13": "6",
  "in.standard.10": "8",
  "in.standard.4.9": "",
  "out.standard.20": "3",
  "out.standard.13": "4",
  "out.standard.10": "2",
  "out.standard.4.9": "",
  "in.eu_intra_community.20": "19",
  "in.eu_intra_community.13": "16",
  "in.eu_intra_community.10": "18",
  "in.eu_intra_community.4.9": "",
  "in.reverse_charge.20": "94",
  "in.reverse_charge.13": "",
  "in.reverse_charge.10": "91",
  "in.reverse_charge.4.9": "",
  "out.export": "1",
  "out.eu_intra_community": "11",
  "out.reverse_charge": "",
};
export const DATEV_KEY_IDS = Object.keys(DATEV_KEYS) as DatevKeyId[];

const accountNumber = z.string().trim().max(20).catch("");
const settingsSchema = z.object({
  tool: z.enum(["", ...EXPORT_TOOLS]).catch(""),
  /** Kasse in the Einheitskontenrahmen. */
  cashAccount: accountNumber.transform((value) => value || "2700"),
  /** First Personenkonto number for suppliers (Kreditoren) and customers (Debitoren). */
  supplierFrom: z.coerce.number().int().positive().optional().catch(undefined),
  customerFrom: z.coerce.number().int().positive().optional().catch(undefined),
  taxCodes: z
    .object({
      bmd: z.record(z.string(), z.string().max(8)).catch({}),
      datev: z.record(z.string(), z.string().max(4)).catch({}),
    })
    .partial()
    .catch({}),
  datev: z
    .object({
      /** Beraternummer (1001-9999999) and Mandantennummer (1-99999) of the Kanzlei. */
      berater: z
        .string()
        .trim()
        .regex(/^\d{0,7}$/)
        .catch(""),
      mandant: z
        .string()
        .trim()
        .regex(/^\d{0,5}$/)
        .catch(""),
      /** Digits of a Sachkonto; Personenkonten have one more. 4 for the Einheitskontenrahmen. */
      accountLength: z.coerce.number().int().min(4).max(8).catch(4),
    })
    .catch({ berater: "", mandant: "", accountLength: 4 }),
});

export type ExportSettings = {
  tool: ExportTool | "";
  cashAccount: string;
  supplierFrom: number;
  customerFrom: number;
  taxCodes: {
    bmd?: Partial<Record<TaxCodeKey, string>>;
    datev?: Partial<Record<DatevKeyId, string>>;
  };
  datev: { berater: string; mandant: string; accountLength: number };
};

/**
 * Personenkonto ranges when none are set: BMD and RZL keep Debitoren at 2xxxx and Kreditoren at
 * 3xxxx in the Einheitskontenrahmen; DATEV wants Debitoren 10000-69999 and Kreditoren 70000-99999
 * with 4-digit Sachkonten.
 */
export function defaultRanges(tool: ExportSettings["tool"]) {
  return tool === "datev"
    ? { supplierFrom: 70000, customerFrom: 10000 }
    : { supplierFrom: 33000, customerFrom: 20000 };
}

export function parseExportSettings(value: unknown): ExportSettings {
  const parsed = settingsSchema.safeParse(value ?? {});
  const data = parsed.success ? parsed.data : settingsSchema.parse({});
  const ranges = defaultRanges(data.tool);
  return {
    tool: data.tool,
    cashAccount: data.cashAccount,
    supplierFrom: data.supplierFrom ?? ranges.supplierFrom,
    customerFrom: data.customerFrom ?? ranges.customerFrom,
    taxCodes: data.taxCodes as ExportSettings["taxCodes"],
    datev: data.datev,
  };
}

/** The code a booking gets: the Kanzlei's override, else the tool's standard. */
export function bmdTaxCode(settings: ExportSettings, key: TaxCodeKey): string {
  return settings.taxCodes.bmd?.[key] ?? BMD_TAX_CODES[key];
}

/** The BU-Schlüssel a DATEV booking gets: the Kanzlei's own, else the known default. */
export function datevKey(settings: ExportSettings, id: DatevKeyId): string {
  return settings.taxCodes.datev?.[id] || DATEV_KEYS[id];
}
